import { Adjustment, OrderBig } from '@chordcommerce/react-autonomy';
import { toLocaleCurrency } from './currency';

// Add interface for grouped promotions
interface GroupedPromotion {
  label: string;
  amount: number;
  code: string | null;
}

interface GroupedPromotions {
  [key: string]: GroupedPromotion;
}

export const eligibleOrderPromotion = (adjustment: Adjustment) => {
  return (
    adjustment.sourceType === 'Spree::PromotionAction' &&
    adjustment.adjustableType === 'Spree::Order' &&
    adjustment.eligible
  );
};

export const eligibleLineItemPromotion = (adjustment: Adjustment) => {
  return (
    adjustment.sourceType === 'Spree::PromotionAction' &&
    adjustment.adjustableType === 'Spree::LineItem' &&
    adjustment.eligible
  );
};

export const getOrderPromotions = (order: Partial<OrderBig>) => {
  if (!order.adjustments || order.adjustments.length === 0) return [];

  return order.adjustments.filter((a) => eligibleOrderPromotion(a));
};

export const getLineItemPromotions = (order: Partial<OrderBig>) => {
  if (!order.lineItems || order.lineItems.length === 0) return [];

  return order.lineItems.reduce<Adjustment[]>((acc, lineItem) => {
    const eligiblePromotions = lineItem.adjustments.filter((a) => eligibleLineItemPromotion(a));
    return acc.concat(eligiblePromotions);
  }, []);
};

export const getAllCartPromotions = (order: Partial<OrderBig>) => {
  if (!order) return [];

  const orderPromotions = getOrderPromotions(order);
  const lineItemPromotions = getLineItemPromotions(order);

  return [...orderPromotions, ...lineItemPromotions];
};

export const getAllCartPromotionsForDisplay = (order: Partial<OrderBig>) => {
  const promotions = getAllCartPromotions(order);

  const groupedPromos = promotions.reduce<GroupedPromotions>(
    (acc, { promotionCodeId, label, amount, promotionCode }) => {
      if (promotionCodeId) {
        // Add null check for promotionCodeId
        acc[promotionCodeId] = acc[promotionCodeId] || {
          label: label,
          amount: 0,
          code: promotionCode ? promotionCode.value : null,
        };
        acc[promotionCodeId].amount += parseFloat(amount);
      }
      return acc;
    },
    {}
  );

  return Object.entries(groupedPromos).map(([key, { label, amount, code }]) => ({
    id: key,
    label: label,
    amount: amount,
    displayAmount: toLocaleCurrency({
      number: amount,
      locale: (order.metadata?.locale as string) || 'en-US',
      currency: order.currency || 'USD',
    }),
    code: code ? code.toUpperCase() : null,
  }));
};
